/**
 * @file User / material analysis UI — pages/Dashboard/User/MaterialAnalysisInput/components/ResultsSection.tsx.
 */

import React from "react";
import { BarChart, type BarLabelProps } from "@mui/x-charts/BarChart";
import { useAnimate } from "@mui/x-charts/hooks";
import { interpolateObject } from "@mui/x-charts-vendor/d3-interpolate";
import { styled } from "@mui/material/styles";

interface ResultsSectionProps {
  totalSubmissions?: number;
  reliableCount?: number;
  dataSpecificityCount?: number;
  dataSpecificityApplied?: boolean;
  hasData?: boolean;
  materialSelected?: boolean;
}

const LabelText = styled("text")({
  stroke: "none",
  fill: "#ffffff",
  transition: "opacity 0.2s ease-in, fill 0.2s ease-in",
  textAnchor: "start",
  dominantBaseline: "central",
  pointerEvents: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
});

function BarLabelInside(props: BarLabelProps) {
  const {
    xOrigin,
    y,
    height,
    width,
    skipAnimation,
    ...otherProps
  } = props;

  const animatedProps = useAnimate(
    { x: xOrigin + 8, y: y + height / 2 },
    {
      initialProps: { x: xOrigin, y: y + height / 2 },
      createInterpolator: interpolateObject,
      transformProps: (p) => p,
      applyProps: (element: SVGTextElement, p) => {
        element.setAttribute("x", p.x.toString());
        element.setAttribute("y", p.y.toString());
      },
      skip: skipAnimation,
    }
  );

  return <LabelText {...otherProps} {...animatedProps} />;
}

/** Exported const — Results Section. */
export const ResultsSection: React.FC<ResultsSectionProps> = ({
  totalSubmissions = 0,
  reliableCount = 0,
  dataSpecificityCount,
  dataSpecificityApplied = false,
  hasData = false,
  materialSelected = false,
}) => {
  const showDataSpecificityBar = dataSpecificityApplied;
  const dsCount = showDataSpecificityBar ? (dataSpecificityCount ?? 0) : 0;
  const CHART_DATA = [
    { label: "Total data pool", value: totalSubmissions },
    { label: "Reliable data pool", value: reliableCount },
    ...(showDataSpecificityBar ? [{ label: "Your specific data pool", value: dsCount }] : []),
  ].map((d) => {
    const count = Math.round(Number(d.value));
    return {
      ...d,
      value: count,
      barValue: count,
    };
  });

  const maxVal = Math.max(...CHART_DATA.map((d) => d.value), 1);
  // Round up axis max to a nice number: 13→15, 16→20, etc. Add 2 units extra headroom.
  const baseAxisMax =
    maxVal <= 5 ? 5 : maxVal <= 20 ? Math.ceil(maxVal / 5) * 5 : Math.ceil(maxVal / 10) * 10;
  const axisMax = baseAxisMax + 2;
  const step = axisMax <= 20 ? 5 : axisMax <= 100 ? 10 : 20;
  const xAxisTickInterval = [];
  for (let i = 0; i <= axisMax; i += step) {
    xAxisTickInterval.push(i);
  }
  if (xAxisTickInterval[xAxisTickInterval.length - 1] !== axisMax) {
    xAxisTickInterval.push(axisMax);
  }

  const DEEP_ORANGE = "#CC6633";

  // Show chart when we have unfiltered data (even if filter returns 0)
  const hasUnfilteredData = totalSubmissions > 0;
  if (!hasUnfilteredData) {
    return (
      <section className="analysis-card analysis-chart-card">
        <div className="analysis-chart-placeholder">
          <p>
            {materialSelected
              ? "no data available, please relax your input conditions or select a different material"
              : "No data available. Please select a material to view results."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="analysis-card analysis-chart-card">
      <div className="analysis-chart">
        <BarChart
          width={undefined}
          dataset={CHART_DATA.map((d) => ({ ...d, value: d.barValue }))}
          height={200}
          series={[
            {
              dataKey: "value",
              color: DEEP_ORANGE,
            },
          ]}
          xAxis={[
            {
              scaleType: "linear",
              position: "bottom",
              label: "Number of Datasets",
              labelStyle: {
                fill: "#5a5a5a",
                fontSize: "0.875rem",
                fontWeight: 600,
              },
              tickLabelStyle: {
                fill: "#5a5a5a",
                fontSize: "0.875rem",
              },
              valueFormatter: (value: number) => String(Math.round(value)),
              tickMinStep: 1,
              tickInterval: xAxisTickInterval,
              tickLabelInterval: () => true,
            },
          ]}
          yAxis={[
            {
              scaleType: "band",
              data: CHART_DATA.map((item) => item.label),
              width: 168,
            },
          ]}
          layout="horizontal"
          margin={{ top: 20, bottom: 55, left: 0, right: 20 }}
          barLabel={(item) => String(item.value ?? 0)}
          slots={{
            barLabel: BarLabelInside,
          }}
          slotProps={{
            bar: {
              clipPath: "inset(0px round 0px)",
            },
          }}
        />
      </div>
    </section>
  );
};
